import { ChevronRight, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { useIssueLinkState } from "../../lib/issue-modal";
import type { IssueDetail, IssuesMeta } from "../../types";
import { SectionHeader } from "../page";
import { Progress } from "../ui/progress";
import { StatusDisc, UserChip } from "./meta";

export function SubIssuesBlock({
  issue,
  meta,
  readOnly = false,
}: {
  issue: IssueDetail;
  meta: IssuesMeta | undefined;
  readOnly?: boolean;
}) {
  const navigate = useNavigate();
  const linkState = useIssueLinkState();
  const done = issue.subIssues.done;
  const total = issue.subIssues.total;
  if (readOnly && issue.children.length === 0) return null;
  return (
    <section aria-label="Sub-issues">
      <SectionHeader
        title="Sub-issues"
        count={total > 0 ? `${done}/${total}` : undefined}
        actions={
          total > 0 && (
            <Progress
              value={(done / total) * 100}
              aria-label={`${done} of ${total} done`}
              className="h-1 w-20 sm:w-28"
            />
          )
        }
        className="mb-1"
      />
      {/* Flat rows: the hover fill and the chevron say they open, no box needed. */}
      <ul className="-mx-2">
        {issue.children.map((child) => {
          const status = meta?.statuses.find((s) => s.key === child.statusKey);
          return (
            <li key={child.id}>
              <Link
                to={`/issues/${child.identifier}`}
                state={linkState}
                className="group/row flex min-h-8 items-center gap-2.5 rounded-md px-2 py-1 text-sm transition-colors duration-(--motion-dur) hover:bg-muted/60"
              >
                <StatusDisc category={status?.category} color={status?.color} />
                <span className="shrink-0 font-mono text-xs whitespace-nowrap text-muted-foreground">
                  {child.identifier}
                </span>
                <span className="min-w-0 truncate decoration-border underline-offset-[3px] group-hover/row:underline">
                  {child.title}
                </span>
                <UserChip user={child.reviewer} className="ml-auto" />
                <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60" />
              </Link>
            </li>
          );
        })}
        {!readOnly && (
          <li>
            <button
              type="button"
              className="flex min-h-8 w-full items-center gap-2.5 rounded-md px-2 py-1 text-left text-sm text-muted-foreground transition-colors duration-(--motion-dur) hover:bg-muted/60 hover:text-foreground"
              onClick={() => navigate(`/issues/new?parent=${encodeURIComponent(issue.identifier)}`)}
            >
              <Plus className="size-3.5" /> Add sub-issue
            </button>
          </li>
        )}
      </ul>
    </section>
  );
}
